import Button from '../shared/Button.js';
// Forecast-only planned batch detail — a LIVE projection keyed by the stable
// forecast monthKey, mounted at /cattle/batches/forecast/<monthKey>. This is
// deliberately NOT a persisted cattle_processing_batches record page: there
// is no record row, no collaboration stream, no rename/date controls. Every
// visit (including direct links and refresh) reconstructs the same projection
// from live data through the scoped cattle.forecast.bundle resource and the
// canonical helpers (forecastFromBundle → projectPlannedRoster), so this page
// can never disagree with the consolidated Planned list.
//
// When the month's planned batch has since been SCHEDULED, this page points
// at the persisted record instead of rendering a shadow copy. When the month
// no longer has a cohort (cattle rolled forward / were hidden / were sent),
// it fails closed with an explicit empty state — never zero/fabricated rows.
import {useAppNavigate as useNavigate} from '../app/navigation/index.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import RecordPageLoadError from '../shared/RecordPageLoadError.jsx';
/* eslint-disable no-unused-vars -- shell primitives are used in JSX only */
import {
  RecordPageFrame,
  RecordPageLoading,
  RecordPageBody,
  RecordBackLink,
  RecordTitle,
} from '../shared/RecordPageShell.jsx';
/* eslint-enable no-unused-vars */
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import Badge from '../shared/Badge.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import ProjectedRosterTable from './ProjectedRosterTable.tsx';
import {parseMonthKey, projectPlannedRoster} from '../lib/cattleForecast.js';
import {forecastFromBundle} from '../lib/cattleForecastApi.js';
import type {CattleForecastBundle} from '../lib/cattleForecastApi.js';
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {CattleForecastBundleData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import Header from '../shared/Header.tsx';

type CattleRecord = Record<string, any>;
type Notice = {kind?: 'error' | 'warning' | 'success' | 'info'; message?: string | null};
type CattleForecastBatchPageProps = {
  sb: any;
  fmt: (...args: any[]) => any;
  authState?: unknown;
  monthKey: string | null;
};

const CATTLE_FORECAST_BUNDLE_PARAMS = Object.freeze({});

export default function CattleForecastBatchPage({fmt, monthKey}: CattleForecastBatchPageProps) {
  const navigate = useNavigate();
  const validMonth = !!parseMonthKey(monthKey || '');
  // A month selects from one canonical bundle; it is not a second cache
  // dimension. Invalid month paths keep the resource disabled and render the
  // dedicated invalid-path state below without issuing a broad forecast read.
  const forecastBundle = useRouteResource(RESOURCE.CATTLE_FORECAST_BUNDLE, CATTLE_FORECAST_BUNDLE_PARAMS, {
    enabled: validMonth,
  });
  const bundleData = forecastBundle.data as CattleForecastBundleData | null;
  const forecast = bundleData ? forecastFromBundle(bundleData as unknown as CattleForecastBundle) : null;
  const loading = forecastBundle.loading;
  const loadAll = forecastBundle.refetch;
  const bundleError = forecastBundle.error as CattleRecord | null;
  const loadError: Notice | null = bundleError
    ? {
        kind: 'error',
        message:
          'Could not load the cattle forecast for this planned batch. Please refresh the page. (' +
          (bundleError.message || String(bundleError)) +
          ')',
      }
    : null;

  const back = () => navigate('/cattle/batches');

  if (!validMonth) {
    return (
      <RecordPageFrame Header={Header}>
        <RecordPageBody maxWidth={900}>
          <RecordBackLink label="Back to Processing Batches" onBack={back} />
          <div
            data-forecast-batch-invalid-month="1"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '1.25rem',
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            Not a valid forecast month.
          </div>
        </RecordPageBody>
      </RecordPageFrame>
    );
  }
  if (loading) {
    return <RecordPageLoading Header={Header} />;
  }
  if (loadError) {
    return (
      <RecordPageLoadError
        backLabel="Back to Processing Batches"
        onBack={back}
        notice={loadError}
        onRetry={loadAll}
        maxWidth={900}
        data-forecast-batch-load-error="true"
      />
    );
  }

  const scheduled = (forecast?.scheduledBatches || []).find((s: CattleRecord) => s && s.monthKey === monthKey) || null;
  const virtual = (forecast?.virtualBatches || []).find((v: CattleRecord) => v && v.monthKey === monthKey) || null;
  // A successful resource resolves its bundle atomically, but preserve a
  // fail-closed UI state if a future runtime ever violates that contract.
  const roster = forecast ? projectPlannedRoster(forecast, monthKey) : {ok: false as const, reason: 'bundle_missing'};

  // The month's planned batch became a persisted scheduled record — point at
  // the real record page rather than rendering a shadow copy of it here.
  if (scheduled) {
    return (
      <RecordPageFrame Header={Header}>
        <RecordPageBody maxWidth={900} data-forecast-batch-scheduled-pointer={scheduled.id}>
          <RecordBackLink label="Back to Processing Batches" onBack={back} />
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '1.25rem',
              fontSize: 13,
              color: 'var(--ink)',
            }}
          >
            This month&apos;s planned batch is now scheduled as <strong>{scheduled.name}</strong>
            {scheduled.planned_process_date ? ' for ' + fmt(scheduled.planned_process_date) : ''}.
            <div style={{marginTop: 10}}>
              <Button
                variant="secondary"
                type="button"
                data-forecast-batch-open-scheduled={scheduled.id}
                onClick={() => navigate('/cattle/batches/' + scheduled.id)}
              >
                Open {scheduled.name}
              </Button>
            </div>
          </div>
        </RecordPageBody>
      </RecordPageFrame>
    );
  }

  if (!virtual || !roster.ok) {
    return (
      <RecordPageFrame Header={Header}>
        <RecordPageBody maxWidth={900}>
          <RecordBackLink label="Back to Processing Batches" onBack={back} />
          <div
            data-forecast-batch-empty="1"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '1.25rem',
              color: 'var(--ink-muted)',
              fontSize: 13,
            }}
          >
            No forecast cohort currently lands in {roster.ok ? roster.label : monthKey}. Cattle may have rolled to a
            different month, been hidden, or already been sent to the processor.
          </div>
        </RecordPageBody>
      </RecordPageFrame>
    );
  }

  return (
    <RecordPageFrame Header={Header}>
      <RecordPageBody maxWidth={900} data-forecast-batch-loaded="true">
        <RecordBackLink label="Back to Processing Batches" onBack={back} />

        <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12}}>
          <RecordTitle fontSize={22} margin={0}>
            {virtual.name}
          </RecordTitle>
          <Badge variant="neutral">Forecast</Badge>
          <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>{virtual.label} est.</span>
          <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>
            {roster.count} {roster.count === 1 ? 'cow' : 'cows'} projected
          </span>
          <span style={{fontSize: 12, fontWeight: 600, color: 'var(--text-primary)'}}>
            {Math.round(roster.projectedTotalLbs).toLocaleString()} lb projected
          </span>
        </div>

        <div
          style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 12,
            color: 'var(--ink-muted)',
          }}
        >
          Forecast-only planned batch — a live projection from the cattle forecast, not a saved record. It updates as
          weigh-ins land, and becomes a record when scheduled from the Planned list or sent from WeighIns.
        </div>

        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '14px 18px',
            marginBottom: 12,
          }}
        >
          <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8}}>
            <span style={{fontSize: 13, fontWeight: 700, color: 'var(--ink)'}}>Projected roster</span>
            <Badge variant="neutral">Projected</Badge>
          </div>
          <ProjectedRosterTable roster={roster} />
        </div>
      </RecordPageBody>
    </RecordPageFrame>
  );
}
